// Custom day layout algorithm that separates availabilities (left zone) from appointments (right zone)
export const customDayLayoutAlgorithm = ({ events, minimumStartDifference, slotMetrics, accessors }) => {
  // Separate events into availabilities and appointments
  const availabilities = events.filter(event => !event.status); // availabilities have no status
  const appointments = events.filter(event => event.status); // appointments have status

  const styledEvents = [];

  // Calculate zone widths based on availability count
  // Tweak these if needed
  const availabilityZoneWidth = availabilities.length > 1 ? 30 : 15; // In multi-view, make availability zone wider so they're not too thin
  const appointmentZoneStart = 10; // Start appointments a bit to the right of availability zone
  const appointmentZoneWidth = 85; // Leave some right margin

  // Helper function to apply no-overlap logic within a zone
  const applyNoOverlapInZone = (zoneEvents, zoneStartPercent, zoneWidthPercent) => {
    if (zoneEvents.length === 0) {
      return [];
    }

    // Create proxy events for positioning
    const proxies = zoneEvents.map(event => ({
      event,
      start: slotMetrics.getRange(accessors.start(event), accessors.end(event)).start,
      end: slotMetrics.getRange(accessors.start(event), accessors.end(event)).end,
      top: slotMetrics.getRange(accessors.start(event), accessors.end(event)).top,
      height: slotMetrics.getRange(accessors.start(event), accessors.end(event)).height,
      friends: [], // For grouping overlapping events
      idx: undefined,
      size: undefined,
    }));

    // Sort by top position and height
    proxies.sort((a, b) => {
      if (a.top !== b.top) {
        return a.top > b.top ? 1 : -1;
      }
      if (a.height !== b.height) {
        return (a.top + a.height) < (b.top + b.height) ? 1 : -1;
      }
      return 0;
    });

    // Build friends graph (overlapping events)
    for (let i = 0; i < proxies.length - 1; ++i) {
      const se1 = proxies[i];
      const y1 = se1.top;
      const y2 = se1.top + se1.height;

      for (let j = i + 1; j < proxies.length; ++j) {
        const se2 = proxies[j];
        const y3 = se2.top;
        const y4 = se2.top + se2.height;

        if ((y3 >= y1 && y4 <= y2) || (y4 > y1 && y4 <= y2) || (y3 >= y1 && y3 < y2)) {
          se1.friends.push(se2);
          se2.friends.push(se1);
        }
      }
    }

    // Assign indices using bitmap
    for (let i = 0; i < proxies.length; ++i) {
      const se = proxies[i];
      const bitmap = [];
      for (let j = 0; j < 100; ++j) {
        bitmap.push(1); // 1 means available
      }

      for (let j = 0; j < se.friends.length; ++j) {
        if (se.friends[j].idx !== undefined) {
          bitmap[se.friends[j].idx] = 0; // 0 means reserved
        }
      }

      se.idx = bitmap.indexOf(1);
    }

    // Calculate sizes
    const getMaxIdxDFS = (node, maxIdx, visited) => {
      for (let i = 0; i < node.friends.length; ++i) {
        if (visited.indexOf(node.friends[i]) > -1) {
          continue;
        }
        maxIdx = maxIdx > node.friends[i].idx ? maxIdx : node.friends[i].idx;
        visited.push(node.friends[i]);
        const newIdx = getMaxIdxDFS(node.friends[i], maxIdx, visited);
        maxIdx = maxIdx > newIdx ? maxIdx : newIdx;
      }
      return maxIdx;
    };

    for (let i = 0; i < proxies.length; ++i) {
      if (proxies[i].size) {
        continue;
      }

      const allFriends = [];
      const maxIdx = getMaxIdxDFS(proxies[i], 0, allFriends);
      const size = zoneWidthPercent / (maxIdx + 1);
      proxies[i].size = size;

      for (let j = 0; j < allFriends.length; ++j) {
        allFriends[j].size = size;
      }
    }

    // Set final positions
    return proxies.map(e => {
      const padding = e.idx === 0 ? 0 : 3;
      return {
        event: e.event,
        style: {
          top: `${e.top}%`,
          height: `calc(${e.height}% - 2px)`,
          width: `calc(${e.size}% - ${padding}px)`,
          xOffset: `calc(${zoneStartPercent}% + ${e.idx * e.size}% + ${padding}px)`,
        },
      };
    });
  };

  // Apply layout to availabilities (left zone)
  styledEvents.push(...applyNoOverlapInZone(availabilities, 0, availabilityZoneWidth));

  // Apply layout to appointments (right zone)
  styledEvents.push(...applyNoOverlapInZone(appointments, appointmentZoneStart, appointmentZoneWidth));

  return styledEvents;
};
